import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {AxiosError, AxiosResponse} from "axios";
import {extractErrorMessage} from "data/utils";
import {IApiResponse} from "data/services/http";
import type {useNavigate} from "react-router-dom";
import {IJoinToLeagueResponse} from "data/providers/api/leagues.api.provider";

interface IParams {
	navigate: ReturnType<typeof useNavigate>;
}

export interface IJoinLeagueCardController extends ViewController<IParams> {
	get i18n(): ILocalizationStore;

	onJoin: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
}

@injectable()
export class JoinLeagueCardController implements IJoinLeagueCardController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _navigate: undefined | IParams["navigate"];

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	private onSuccessJoined = (response: AxiosResponse<IJoinToLeagueResponse>) => {
		const {id} = response.data.success;

		if (this._navigate) {
			this._navigate(`/league/${id}/standings`);
		}
	};

	private onError = (error: AxiosError<IApiResponse>) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
	};

	private onShowJoinedModal = () => {
		setTimeout(() => {
			this._modalsStore.showModal(ModalType.LEAGUE_JOINED, {
				title: this._i18nStore.t("modal.league_joined.title", "CONGRATULATIONS"),
				message: this._i18nStore.t(
					"modal.league_joined.description",
					"You have now joined this league. Good luck!"
				),
			});
		}, 1000);
	};

	@action onJoin = (e: React.SyntheticEvent<HTMLButtonElement>): void => {
		const code = e.currentTarget.value;

		if (!code) {
			return;
		}

		void this._leaguesStore
			.joinToLeagueWithRedirect({code})
			.then(this.onSuccessJoined)
			.then(this.onShowJoinedModal)
			.catch(this.onError);
	};

	dispose() {
		return;
	}

	init(param: IParams) {
		this._navigate = param.navigate;
	}
}
